import React, { useEffect, useRef, useState } from 'react';
import CustomDialog from '@configuratorware/configurator-frontendgui/Shared/Components/CustomDialog';
import MainButton from '@configuratorware/configurator-frontendgui/Shared/Components/MainButton';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import { Services } from '@configuratorware/configurator-frontendgui/App/ServiceLocator';
import { getShareUrl } from '@configuratorware/configurator-frontendgui/App/Reducers/Configurator/Selectors';
import CircularProgress from '@material-ui/core/CircularProgress';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import TextField from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import Check from '@material-ui/icons/Check';
import CheckCircle from '@material-ui/icons/CheckCircle';
import Error from '@material-ui/icons/Error';
import FileCopy from '@material-ui/icons/FileCopy';
import { useSelector } from 'react-redux';
import { darkBlue } from '../../Constants/Colors';
import { copyToClipboard } from '../../utils/clipboard';

type CopyState = 'idle' | 'copied' | 'failed';

const COPIED_FEEDBACK_DURATION = 3000;

const useStyles = makeStyles((theme) => ({
  content: {
    padding: '0 24px 0 12px',
  },
  caption: {
    fontSize: 12,
    lineHeight: 1.7,
  },
  code: {
    textAlign: 'center',
    marginTop: 24,
    marginBottom: 24,
    fontSize: 24,
    lineHeight: '28px',
  },
  successResult: {
    background: '#4FC36D',
    color: '#fff',
    padding: '14px 8px',
    borderRadius: 4,
  },
  successResultIcon: {
    float: 'left',
  },
  errorResult: {
    background: '#ED1C29',
    color: '#fff',
    padding: '14px 8px',
    borderRadius: 4,
    fontSize: 14,
    lineHeight: 1.7,
    textAlign: 'center',
  },
  errorResultIcon: {
    float: 'left',
  },
  progress: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 24,
  },
  shareUrlLabel: {
    display: 'block',
    fontSize: 12,
    lineHeight: 1.7,
    marginBottom: 4,
  },
  shareUrlInput: {
    fontSize: 14,
  },
  copyButton: {
    // the theme renders icon buttons white (header)
    color: darkBlue,
  },
  copyFeedback: {
    minHeight: 24,
    marginTop: 4,
    marginBottom: 16,
    fontSize: 12,
    lineHeight: 1.7,
  },
  copied: {
    color: '#2E7D32',
  },
  failed: {
    color: theme.palette.error.main,
  },
}));

// same state as the vendor SaveConfiguration container
const isVisible = (state) => !!state.ui.visibilityMap.saveConfiguration;
const isSaving = (state) => !!state.ui.backgroundWorkMap.saveConfiguration;
const isLoadingLoginInfo = (state) => !!state.ui.backgroundWorkMap.loadLoginInfo;
const getErrorMessage = (state) => state.ui.error?.message;
const getCode = (state) => state.configurator.configuration?.code || '';
const getConfigurationShareUrl = (state): string => getShareUrl(state.configurator);

export const SaveConfigurationDialog = () => {
  const classes = useStyles();
  const show = useSelector(isVisible);
  const workInProgress = useSelector(isSaving);
  const loadLoginInfoInProgress = useSelector(isLoadingLoginInfo);
  const errorMessage = useSelector(getErrorMessage);
  const code = useSelector(getCode);
  const shareUrl = useSelector(getConfigurationShareUrl);

  const [copyState, setCopyState] = useState<CopyState>('idle');
  const shareUrlInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (show) setCopyState('idle');
  }, [show, shareUrl]);

  useEffect(() => {
    if (copyState !== 'copied') return;
    const timeout = setTimeout(() => setCopyState('idle'), COPIED_FEEDBACK_DURATION);
    return () => clearTimeout(timeout);
  }, [copyState]);

  const close = () => Services.ui.hide('saveConfiguration');

  const handleCopy = async () => {
    const copied = await copyToClipboard(shareUrl, shareUrlInputRef.current);
    setCopyState(copied ? 'copied' : 'failed');

    if (!copied) {
      // keep the link selected so the user can copy it manually
      shareUrlInputRef.current?.focus();
      shareUrlInputRef.current?.select();
    }
  };

  const selectShareUrl = (event: React.SyntheticEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    event.currentTarget.select();

  return (
    <CustomDialog
      open={show}
      onClose={close}
      onCancel={close}
      dialogTitle={t('saveConfiguration.dialogTitle')}
    >
      <DialogContent className={classes.content}>
        {workInProgress && (
          <div className={classes.progress}>
            <CircularProgress />
          </div>
        )}
        {errorMessage && (
          <div className={classes.errorResult}>
            <Error className={classes.errorResultIcon} />
            {t(errorMessage)}
          </div>
        )}
        {!workInProgress && code && (
          <>
            <Typography variant="body1" className={classes.caption}>
              {t('saveConfiguration.successfullySaved')}
            </Typography>
            <Typography variant="body1" className={classes.caption}>
              {t('saveConfiguration.infoText')}
            </Typography>
            <Typography variant="h5" component="div" className={classes.code}>
              <div className={classes.successResult}>
                <CheckCircle className={classes.successResultIcon} />
                <b>{code}</b>
              </div>
            </Typography>
            {shareUrl && (
              <>
                <Typography
                  variant="body1"
                  component="label"
                  htmlFor="share-url"
                  className={classes.shareUrlLabel}
                >
                  {t('saveConfiguration.shareUrlLabel')}
                </Typography>
                <TextField
                  id="share-url"
                  inputRef={shareUrlInputRef}
                  value={shareUrl}
                  variant="outlined"
                  size="small"
                  fullWidth
                  inputProps={{ onFocus: selectShareUrl, onClick: selectShareUrl }}
                  InputProps={{
                    readOnly: true,
                    className: classes.shareUrlInput,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip
                          title={
                            copyState === 'copied'
                              ? t('saveConfiguration.linkCopied')
                              : t('saveConfiguration.copyConfigurationLink')
                          }
                        >
                          <IconButton
                            edge="end"
                            size="small"
                            className={classes.copyButton}
                            aria-label={t('saveConfiguration.copyConfigurationLink')}
                            onClick={handleCopy}
                          >
                            {copyState === 'copied' ? <Check /> : <FileCopy />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography
                  variant="body1"
                  aria-live="polite"
                  className={`${classes.copyFeedback} ${
                    copyState === 'failed' ? classes.failed : classes.copied
                  }`}
                >
                  {copyState === 'copied' && t('saveConfiguration.linkCopied')}
                  {copyState === 'failed' && t('saveConfiguration.copyFailed')}
                </Typography>
              </>
            )}
            {loadLoginInfoInProgress && (
              <Typography variant="body1" className={classes.caption}>
                {t('saveConfiguration.loadLoginInfoInProgressText')}
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {!workInProgress && code && <MainButton onClick={close}>{t('saveConfiguration.okText')}</MainButton>}
      </DialogActions>
    </CustomDialog>
  );
};
